import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import moment from 'moment';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import { debounce } from 'lodash';
import Title from 'components/Title';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import Status from 'components/Status';
import Priority from 'components/Priority';
import { TasksActions } from 'state/modules/tasks';
import SearchIcon from '@mui/icons-material/Search';
import { SearchField } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';
import { UsersSelect } from 'components/AsyncFields';
import { toQueryString, getDynamicLink } from 'utils';
import { Box, Grid, Paper, Container, Typography, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TasksStatusesSelect, PrioritySelect } from 'components/DataFields';

const LinkBox = styled(Box)(({ theme }) => ({
  '& a': {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
}));

const TasksList = () => {
  const urlParams = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const localeFormat = useSelector((state) => state.organizations.localeFormat);
  const list = useSelector((state) => state.tasks.list);
  const loading = useSelector((state) => state.tasks.loading);
  const pagination = useSelector((state) => state.tasks.pagination);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  /**
   * Table columns
   * Includes fleet and service groups
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: (row) => row.id,
      cell: (row) => {
        return (
          <Typography onClick={() => handleRowClick(row)} sx={{ cursor: 'pointer' }}>
            <Box fontSize={13} component="span">
              T{row.id}
            </Box>
          </Typography>
        );
      },
      grow: 0,
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.priority'),
      selector: (row) => row.priority,
      cell: (row) => {
        return (
          <Box onClick={() => handleRowClick(row)} sx={{ cursor: 'pointer' }}>
            <Priority priority={row.priority} />
          </Box>
        );
      },
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.title'),
      selector: (row) => row.title,
      cell: (row) => {
        return (
          <Typography
            sx={{ cursor: 'pointer' }}
            component="div"
            onClick={() => handleRowClick(row)}
          >
            <Box fontSize={13}>{row.title}</Box>
          </Typography>
        );
      },
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.related_to'),
      cell: (row) => {
        return (
          <>
            {row.task_source_name && row.task_source_id ? (
              <LinkBox fontSize={13} fontWeight="fontWeightMedium">
                {getDynamicLink(
                  i18n.t(`common.${row.task_source_name}`),
                  row.task_source_id,
                  row.task_source_name
                )}
              </LinkBox>
            ) : (
              <Typography component="div">
                <Box fontSize={13} fontWeight="fontWeightMedium">
                  --
                </Box>
              </Typography>
            )}
          </>
        );
      },
    },
    {
      name: i18n.t('tableColumns.assignee'),
      cell: (row) => {
        return (
          <Typography
            component="div"
            sx={{ cursor: 'pointer' }}
            onClick={() => handleRowClick(row)}
          >
            <Box fontSize={13} fontWeight="fontWeightRegular">
              {row.owner_user.first_name} {row.owner_user.last_name}
            </Box>
          </Typography>
        );
      },
    },
    {
      name: i18n.t('tableColumns.alarm'),
      cell: (row) => {
        return (
          <Box sx={{ cursor: 'pointer' }}>
            {row.parent_alarm_id ? (
              <LinkBox fontSize={13} fontWeight="fontWeightMedium">
                {getDynamicLink(
                  `A - ${row.parent_alarm_id}`,
                  row.parent_alarm_id,
                  'ConditionalInstance'
                )}
              </LinkBox>
            ) : (
              <Typography component="div" onClick={() => handleRowClick(row)}>
                <Box fontSize={13} fontWeight="fontWeightMedium">
                  --
                </Box>
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      name: i18n.t('tableColumns.due_date'),
      selector: (row) => row.due_time,
      cell: (row) => {
        return (
          <>
            {row.due_time ? (
              <Typography
                component="div"
                sx={{ cursor: 'pointer' }}
                onClick={() => handleRowClick(row)}
              >
                <Box
                  fontSize={13}
                  display="flex"
                  alignItems="center"
                  fontWeight="fontWeightMedium"
                  sx={{ color: row.overdue ? (theme) => theme.palette.error.main : 'inherit' }}
                >
                  {moment(row.due_time).format(`${localeFormat} hh:mm A`)}
                </Box>
              </Typography>
            ) : (
              <Typography component="div" onClick={() => handleRowClick(row)}>
                <Box fontSize={13} fontWeight="fontWeightMedium">
                  --
                </Box>
              </Typography>
            )}
          </>
        );
      },
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.status'),
      selector: (row) => row.status,
      cell: (row) => (
        <Box onClick={() => handleRowClick(row)} sx={{ cursor: 'pointer' }}>
          <Status status={row.status} statusKey="tasks_statuses" fontWeight="fontWeightRegular" />
        </Box>
      ),
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row) => row.status === 'COMPLETED',
      style: {
        opacity: 0.5,
      },
    },
  ];

  /**
   * Get vehicles grpups list
   * @param {object} params Request params
   */
  const fetchTasks = useCallback(
    (params = {}) => {
      const _params = { ...params };
      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(TasksActions.fetchTasks(_params, { signal: abortController?.current?.signal }));
    },
    [dispatch]
  );

  const fetchTasksDebounced = useCallback(
    debounce((params) => fetchTasks(params), 1000),
    [fetchTasks]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchTasksDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchTasksDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchTasks(params);
  }, []);

  const handleParams = (params) => {
    const data = { ...params };
    setParams({ ...data });
    const _params = toQueryString(data);
    history.replace(buildUrl(`/tasks${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => (!filters[key] || filters[key] === '') && delete filters[key]
    );
    handleParams(filters);
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/tasks/${row?.id}`));
  };

  return (
    <Layout title={i18n.t('tasks.title')}>
      <Container sx={{ minWidth: '100%', m: 0 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid size={{ sm: 8 }}>
                <Title title={i18n.t('tasks.title')} data-cy="title" />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ paddingBottom: '25px' }}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <SearchField
                  label={i18n.t('common.search')}
                  margin="none"
                  field={{
                    name: 'keyword',
                    onChange: search,
                    value: params.keyword,
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        sx={{
                          '& svg': {
                            color: 'secondary.light',
                            fontSize: (theme) => theme.typography.pxToRem(14),
                          },
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TasksStatusesSelect
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'statuses',
                    value: params.statuses,
                  }}
                  margin="none"
                  label={i18n.t('common.status')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <UsersSelect
                  type="assigned"
                  id="tasksAssignedSelect"
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'assigned_user_ids',
                    value: params?.assigned_user_ids || '',
                  }}
                  margin="none"
                  label={i18n.t('tasks.assignee')}
                  customParams={{ is_customer: false }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <UsersSelect
                  type="creators"
                  id="tasksCreatorsSelect"
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'creator_id',
                    value: params?.creator_id || '',
                  }}
                  margin="none"
                  label={i18n.t('tasks.creator')}
                  customParams={{ is_customer: false }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <PrioritySelect
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'priority',
                    value: params?.priority || '',
                  }}
                  margin="none"
                  label={i18n.t('common.priority')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper elevation={2} sx={{ boxShadow: '0px 3px 6px #0000001A' }}>
              <Table
                data={list}
                columns={columns}
                loading={loading}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                paginationDefaultPage={params?.page || 1}
                handlePerRowsChange={handlePerRowsChange}
                conditionalRowStyles={conditionalRowStyles}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default TasksList;
